import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Segment,
  Card,
  Icon,
  Label,
  Message,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders } from '../../api';

const AUDIENCE_LABELS = {
  public: { color: 'green', icon: 'globe', text: 'Public' },
  authenticated: { color: 'blue', icon: 'user', text: 'Users' },
  internal: { color: 'purple', icon: 'building', text: 'Internal Governance' },
  contributor: { color: 'teal', icon: 'translate', text: 'Contributors' },
  admin: { color: 'orange', icon: 'shield', text: 'Administrators' },
};

const TYPE_LABELS = {
  POL: 'Policy',
  SOP: 'Procedure',
  WI: 'Work Instruction',
  GUIDE: 'Guide',
  REF: 'Reflection',
  REC: 'Reflection',
};

export default function PolicyPortal() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPolicies() {
      try {
        const headers = getAuthHeaders();
        const res = await fetch(`${getApiPath()}/@polyglot/policies`, {
          headers: Object.keys(headers).length > 0 ? headers : undefined,
        });
        if (!res.ok) throw new Error('Failed to load policies');
        const data = await res.json();
        setPolicies(data.items || []);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    fetchPolicies();
  }, []);

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment loading placeholder style={{ minHeight: '200px' }} />
      </Container>
    );
  }

  // One flat section: all accessible policies + guides in a single grid.
  // The per-card audience chip still shows the access tier (DR-2026-180).
  return (
    <Container style={{ paddingTop: '2em' }}>
      <Helmet title="Policies and Guides" />
      <div className="polyglot-page-header">
        <h1>Policies and Guides</h1>
        <p className="subtitle">Project policies, procedures, and guidelines</p>
      </div>

      {error && <Message error>{error}</Message>}

      {policies.length === 0 && !error && (
        <Message info>
          <Message.Header>Nothing available</Message.Header>
          <p>There are no documents accessible at your current access level.</p>
        </Message>
      )}

      <Card.Group itemsPerRow={2} stackable>
        {policies.map((p) => {
          const label = AUDIENCE_LABELS[p.audience] || AUDIENCE_LABELS.admin;
          return (
            <Card key={p.doc_id} as={Link} to={`/policies/${p.doc_id}`}>
              <Card.Content>
                <Card.Header>{p.title}</Card.Header>
                <Card.Meta>
                  <Label size="tiny" color={label.color}>
                    {label.text}
                  </Label>
                  <Label size="tiny">
                    {TYPE_LABELS[p.document_type] || p.document_type}
                  </Label>
                  {p.version && (
                    <Label size="tiny" basic>
                      v{p.version}
                    </Label>
                  )}
                </Card.Meta>
              </Card.Content>
              <Card.Content extra>
                <Icon name="file text" /> {p.doc_id}
                {p.last_updated && (
                  <span style={{ float: 'right', color: '#999' }}>
                    <Icon name="clock" />
                    {new Date(p.last_updated).toLocaleDateString()}
                  </span>
                )}
              </Card.Content>
            </Card>
          );
        })}
      </Card.Group>
    </Container>
  );
}
